import { useState } from 'react';
import { formatTinNumber } from '../../../../utils/tin-number-formater.utils';

type BasicInfoData = {
    groupName?: string | null;
    natureOfBusiness?: string | null;
    noOfLives?: string | number | null;
    completeBusinessAddress?: string | null;
    proposalAddress?: string | null;
    addressDesignation?: string | null;
    tinNumber?: string | null;
    secNumber?: string | null;
    companyContactNumberName?: string | null;
    companyMobileNo?: string | null;
    companyTelephone?: string | null;
    contactPersonSalutation?: string | null;
    contactPersonFirstName?: string | null;
    contactPersonMiddleName?: string | null;
    contactPersonLastName?: string | null;
    contactPersonNumberName?: string | null;
    contactNumber?: string | null;
    contactTelephone?: string | null;
    email?: string | null;
    designation?: string | null;
};

type Affiliation = {
    id?: number | string | null;
    name?: string | null;
    address?: string | null;
    tinNumber?: string | null;
};

interface Props {
    isShow: boolean;
    currentInfo: BasicInfoData;
    proposedInfo: BasicInfoData;
    currentAffiliations?: Affiliation[];
    proposedAffiliations?: Affiliation[];
}

type ChangeItem = {
    key: string;
    label: string;
    current: string;
    proposed: string;
};

type AffiliationChange = {
    key: string;
    current?: Affiliation;
    proposed?: Affiliation;
    changes: ChangeItem[];
};

const normalizeValue = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
};

const isDifferent = (current: unknown, proposed: unknown): boolean => {
    return normalizeValue(current) !== normalizeValue(proposed);
};

const formatContactPersonName = (info: BasicInfoData): string => {
    const salutation = normalizeValue(info.contactPersonSalutation);
    const firstName = normalizeValue(info.contactPersonFirstName);
    const middleName = normalizeValue(info.contactPersonMiddleName);
    const lastName = normalizeValue(info.contactPersonLastName);
    const middleInitial = middleName ? `${middleName.charAt(0)}.` : '';

    return `${salutation} ${firstName} ${middleInitial} ${lastName}`.replace(/\s+/g, ' ').trim();
};

const formatCompanyContactNumber = (info: BasicInfoData): string => {
    const mobile = normalizeValue(info.companyMobileNo);
    const telephone = normalizeValue(info.companyTelephone);

    if (mobile) return `+63${mobile}`;
    return telephone;
};

const formatContactPersonNumber = (info: BasicInfoData): string => {
    const mobile = normalizeValue(info.contactNumber);
    const telephone = normalizeValue(info.contactTelephone);

    if (mobile) return `+63${mobile}`;
    return telephone;
};

const formatTin = (value: unknown): string => {
    const normalized = normalizeValue(value);
    return normalized ? formatTinNumber(normalized) : '';
};

const getCompanyChanges = (currentInfo: BasicInfoData, proposedInfo: BasicInfoData): ChangeItem[] => {
    const changes: ChangeItem[] = [];

    const fields = [
        { key: 'groupName', label: 'SEC Registered Name', current: currentInfo.groupName, proposed: proposedInfo.groupName },
        { key: 'natureOfBusiness', label: 'Nature of Business', current: currentInfo.natureOfBusiness, proposed: proposedInfo.natureOfBusiness },
        { key: 'noOfLives', label: 'No. of Lives', current: currentInfo.noOfLives, proposed: proposedInfo.noOfLives },
        { key: 'completeBusinessAddress', label: 'Complete Business Address', current: currentInfo.completeBusinessAddress, proposed: proposedInfo.completeBusinessAddress },
        { key: 'proposalAddress', label: 'Proposal Addressee', current: currentInfo.proposalAddress, proposed: proposedInfo.proposalAddress },
        { key: 'addressDesignation', label: 'Addressee Designation', current: currentInfo.addressDesignation, proposed: proposedInfo.addressDesignation },
        { key: 'tinNumber', label: 'TIN Number', current: formatTin(currentInfo.tinNumber), proposed: formatTin(proposedInfo.tinNumber) },
        { key: 'secNumber', label: 'SEC Number', current: currentInfo.secNumber, proposed: proposedInfo.secNumber },
    ];

    fields.forEach((field) => {
        if (isDifferent(field.current, field.proposed)) {
            changes.push({
                key: field.key,
                label: field.label,
                current: normalizeValue(field.current),
                proposed: normalizeValue(field.proposed),
            });
        }
    });

    const currentCompanyContactType = normalizeValue(currentInfo.companyContactNumberName);
    const proposedCompanyContactType = normalizeValue(proposedInfo.companyContactNumberName);
    const currentCompanyContactNumber = formatCompanyContactNumber(currentInfo);
    const proposedCompanyContactNumber = formatCompanyContactNumber(proposedInfo);

    const companyContactTypeChanged = isDifferent(currentCompanyContactType, proposedCompanyContactType);
    const companyContactNumberChanged = isDifferent(currentCompanyContactNumber, proposedCompanyContactNumber);

    if (companyContactTypeChanged || companyContactNumberChanged) {
        changes.push({
            key: 'companyContactNumber',
            label: 'Company Contact Number',
            current: normalizeValue(currentCompanyContactNumber),
            proposed: normalizeValue(proposedCompanyContactNumber),
        });
    }

    return changes;
};

const getContactChanges = (currentInfo: BasicInfoData, proposedInfo: BasicInfoData): ChangeItem[] => {
    const changes: ChangeItem[] = [];

    const fields = [
        { key: 'fullName', label: 'Full Name', current: formatContactPersonName(currentInfo), proposed: formatContactPersonName(proposedInfo) },
        { key: 'contactNumber', label: `${proposedInfo.contactPersonNumberName ?? currentInfo.contactPersonNumberName ?? 'Contact'} Number`, current: formatContactPersonNumber(currentInfo), proposed: formatContactPersonNumber(proposedInfo) },
        { key: 'email', label: 'Email', current: currentInfo.email, proposed: proposedInfo.email },
        { key: 'designation', label: 'Designation', current: currentInfo.designation, proposed: proposedInfo.designation },
    ];

    fields.forEach((field) => {
        if (isDifferent(field.current, field.proposed)) {
            changes.push({
                key: field.key,
                label: field.label,
                current: normalizeValue(field.current),
                proposed: normalizeValue(field.proposed),
            });
        }
    });

    return changes;
};

const hasAffiliationValue = (item?: Affiliation): boolean => {
    if (!item) return false;

    return Boolean(
        normalizeValue(item.name) ||
        normalizeValue(item.address) ||
        normalizeValue(item.tinNumber),
    );
};

const getAffiliationKey = (item: Affiliation): string => {
    const id = normalizeValue(item.id);

    if (id) return `id:${id}`;

    return `value:${normalizeValue(item.name)}|${normalizeValue(item.address)}|${normalizeValue(item.tinNumber)}`;
};

const getAffiliationFields = (current: Affiliation | undefined, proposed: Affiliation | undefined): ChangeItem[] => {
    const changes: ChangeItem[] = [];

    if (isDifferent(current?.name, proposed?.name)) {
        changes.push({
            key: 'name',
            label: 'Affiliation Name',
            current: normalizeValue(current?.name),
            proposed: normalizeValue(proposed?.name),
        });
    }

    if (isDifferent(current?.address, proposed?.address)) {
        changes.push({
            key: 'address',
            label: 'Address',
            current: normalizeValue(current?.address),
            proposed: normalizeValue(proposed?.address),
        });
    }

    const currentTin = formatTin(current?.tinNumber);
    const proposedTin = formatTin(proposed?.tinNumber);

    if (isDifferent(currentTin, proposedTin)) {
        changes.push({
            key: 'tinNumber',
            label: 'TIN Number',
            current: currentTin,
            proposed: proposedTin,
        });
    }

    return changes;
};

const getAffiliationChanges = (currentAffiliations: Affiliation[], proposedAffiliations: Affiliation[]): AffiliationChange[] => {
    const currentValid = currentAffiliations.filter(hasAffiliationValue);
    const proposedValid = proposedAffiliations.filter(hasAffiliationValue);

    const currentGroups: Record<string, Affiliation[]> = {};
    const proposedGroups: Record<string, Affiliation[]> = {};

    currentValid.forEach((item) => {
        const key = getAffiliationKey(item);

        if (!currentGroups[key]) currentGroups[key] = [];

        currentGroups[key].push(item);
    });

    proposedValid.forEach((item) => {
        const key = getAffiliationKey(item);

        if (!proposedGroups[key]) proposedGroups[key] = [];

        proposedGroups[key].push(item);
    });

    const allKeys: string[] = [];

    Object.keys(currentGroups).forEach((key) => {
        if (allKeys.indexOf(key) === -1) allKeys.push(key);
    });

    Object.keys(proposedGroups).forEach((key) => {
        if (allKeys.indexOf(key) === -1) allKeys.push(key);
    });

    const result: AffiliationChange[] = [];

    allKeys.forEach((key) => {
        const currentItems = currentGroups[key] ?? [];
        const proposedItems = proposedGroups[key] ?? [];
        const maxLength = Math.max(currentItems.length, proposedItems.length);

        for (let index = 0; index < maxLength; index++) {
            const currentItem = currentItems[index];
            const proposedItem = proposedItems[index];

            if (!currentItem && proposedItem) {
                result.push({
                    key: `${key}-added-${index}`,
                    proposed: proposedItem,
                    changes: [
                        {
                            key: 'new',
                            label: 'Affiliated Company',
                            current: '',
                            proposed: normalizeValue(proposedItem.name),
                        },
                    ],
                });

                continue;
            }

            if (currentItem && !proposedItem) {
                result.push({
                    key: `${key}-removed-${index}`,
                    current: currentItem,
                    changes: [
                        {
                            key: 'removed',
                            label: 'Affiliated Company',
                            current: normalizeValue(currentItem.name),
                            proposed: '',
                        },
                    ],
                });

                continue;
            }

            if (currentItem && proposedItem) {
                const changes = getAffiliationFields(currentItem, proposedItem);

                if (changes.length > 0) {
                    result.push({
                        key: `${key}-changed-${index}`,
                        current: currentItem,
                        proposed: proposedItem,
                        changes,
                    });
                }
            }
        }
    });

    return result;
};

const NoChanges = () => {
    return (
        <div className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-3 text-[11px] text-gray-400">
            <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 shrink-0" aria-hidden="true">
                <path d="m5 12.5 4.5 4L19 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>No changes</span>
        </div>
    );
};

const ChangeValue = ({ label, value, proposed = false }: { label: string; value: string; proposed?: boolean }) => {
    return (
        <div className="min-w-0">
            <div className={`mb-1 text-[9px] font-semibold uppercase tracking-wide ${proposed ? 'text-[#186cb2]' : 'text-gray-400'}`}>
                {label}
            </div>

            <div className="break-words text-[11px] leading-relaxed text-gray-700">
                {value || 'Not provided'}
            </div>
        </div>
    );
};

const ComparisonSection = ({ title, changes, showContent, onToggle }: { title: string; changes: ChangeItem[]; showContent: boolean; onToggle: () => void }) => {
    return (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <button type="button" onClick={onToggle} className="flex w-full items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-3 py-2.5 text-left transition-colors hover:bg-gray-100">
                <div className="flex min-w-0 items-center gap-2">
                    <svg viewBox="0 0 24 24" fill="none" className={`h-3.5 w-3.5 shrink-0 text-[#186cb2] transition-transform ${showContent ? 'rotate-0' : '-rotate-90'}`} aria-hidden="true">
                        <path d="m7 10 5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>

                    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                        {title}
                    </h3>

                    <span className="rounded-full bg-white px-1.5 py-0.5 text-[8px] font-bold text-[#186cb2] ring-1 ring-[#c7e0f3]">
                        {changes.length}
                    </span>
                </div>

                <span className="shrink-0 text-[9px] font-medium text-gray-400">
                    {showContent ? 'Hide' : 'Show'}
                </span>
            </button>
            <div className="grid grid-cols-1 border-b border-slate-200 sm:grid-cols-2">
                <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 sm:border-b-0 sm:border-r">
                    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Current
                    </h3>
                </div>
                <div className="bg-[#f5f9fc] px-4 py-2.5">
                    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[#186cb2]">
                        Proposed
                    </h3>
                </div>
            </div>
            {showContent && (
                <div className="grid grid-cols-1 sm:grid-cols-2">
                    <div className="min-w-0 border-b border-gray-200 p-3 sm:border-b-0 sm:border-r sm:p-4">
                        {changes.length === 0 ? (
                            <NoChanges />
                        ) : (
                            <div className="space-y-3">
                                {changes.map((item) => (
                                    <ChangeValue
                                        key={`current-${item.key}`}
                                        label={item.label}
                                        value={item.current}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="min-w-0 p-3 sm:p-4">
                        {changes.length === 0 ? (
                            <NoChanges />
                        ) : (
                            <div className="space-y-3">
                                {changes.map((item) => (
                                    <ChangeValue
                                        key={`proposed-${item.key}`}
                                        label={item.label}
                                        value={item.proposed}
                                        proposed
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const AffiliationDetail = ({ label, value, proposed = false }: { label: string; value?: string | null; proposed?: boolean }) => {
    return (
        <div className="min-w-0">
            <div className={`mb-1 text-[9px] font-semibold uppercase tracking-wide ${proposed ? 'text-[#186cb2]' : 'text-gray-400'}`}>
                {label}
            </div>

            <div className="break-words text-[11px] leading-relaxed text-gray-700">
                {normalizeValue(value) || 'Not provided'}
            </div>
        </div>
    );
};

const AffiliationDetails = ({ affiliation, proposed = false }: { affiliation?: Affiliation; proposed?: boolean }) => {
    if (!affiliation) {
        return (
            <div className="flex min-h-[90px] items-center rounded-md bg-gray-50 px-3 py-3 text-[11px] text-gray-400">
                Affiliated company not provided
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-3 rounded-md border border-gray-200 bg-white p-3 sm:grid-cols-2 lg:grid-cols-3">
            <AffiliationDetail label="Affiliation Name" value={affiliation.name} proposed={proposed} />
            <AffiliationDetail label="Address" value={affiliation.address} proposed={proposed} />
            <AffiliationDetail label="TIN Number" value={formatTin(affiliation.tinNumber)} proposed={proposed} />
        </div>
    );
};

const AffiliationComparisonSection = ({ changes, showContent, onToggle }: { changes: AffiliationChange[]; showContent: boolean; onToggle: () => void }) => {
    return (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <button type="button" onClick={onToggle} className="flex w-full items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-3 py-2.5 text-left transition-colors hover:bg-gray-100">
                <div className="flex min-w-0 items-center gap-2">
                    <svg viewBox="0 0 24 24" fill="none" className={`h-3.5 w-3.5 shrink-0 text-[#186cb2] transition-transform ${showContent ? 'rotate-0' : '-rotate-90'}`} aria-hidden="true">
                        <path d="m7 10 5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                        Affiliated Companies
                    </h3>
                    <span className="rounded-full bg-white px-1.5 py-0.5 text-[8px] font-bold text-[#186cb2] ring-1 ring-[#c7e0f3]">
                        {changes.length}
                    </span>
                </div>
                <span className="shrink-0 text-[9px] font-medium text-gray-400">
                    {showContent ? 'Hide' : 'Show'}
                </span>
            </button>
            <div className="grid grid-cols-1 border-b border-slate-200 sm:grid-cols-2">
                <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 sm:border-b-0 sm:border-r">
                    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Current
                    </h3>
                </div>
                <div className="bg-[#f5f9fc] px-4 py-2.5">
                    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[#186cb2]">
                        Proposed
                    </h3>
                </div>
            </div>
            {showContent && (
                <div className="grid grid-cols-1 sm:grid-cols-2">
                    {/* CURRENT */}
                    <div className="min-w-0 border-b border-gray-200 p-3 sm:border-b-0 sm:border-r sm:p-4">
                        {changes.length === 0 ? (
                            <NoChanges />
                        ) : (
                            <div className="space-y-4">
                                {changes.map((item, index) => (
                                    <div key={`current-${item.key}`} className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                                        <div className="mb-2 flex items-center gap-2">
                                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[9px] font-semibold text-gray-500">
                                                {index + 1}
                                            </span>
                                            <span className="min-w-0 break-words text-[11px] font-semibold text-gray-700">
                                                {item.current?.name || 'Affiliated Company'}
                                            </span>
                                        </div>
                                        <AffiliationDetails affiliation={item.current} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {/* PROPOSED */}
                    <div className="min-w-0 p-3 sm:p-4">
                        {changes.length === 0 ? (
                            <NoChanges />
                        ) : (
                            <div className="space-y-4">
                                {changes.map((item, index) => (
                                    <div key={`proposed-${item.key}`} className="border-b border-blue-50 pb-4 last:border-b-0 last:pb-0">
                                        <div className="mb-2 flex items-center gap-2">
                                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#e3f1fa] text-[9px] font-semibold text-[#186cb2]">
                                                {index + 1}
                                            </span>
                                            <span className="min-w-0 break-words text-[11px] font-semibold text-gray-700">
                                                {item.proposed?.name || 'Affiliated Company'}
                                            </span>
                                        </div>
                                        <AffiliationDetails affiliation={item.proposed} proposed />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default function ProposedAmendClientBasicInformation({
    isShow,
    currentInfo,
    proposedInfo,
    currentAffiliations = [],
    proposedAffiliations = [],
}: Props) {
    const [showContent, setShowContent] = useState(true);
    const [companyOpen, setCompanyOpen] = useState(true);
    const [contactOpen, setContactOpen] = useState(true);
    const [affiliationsOpen, setAffiliationsOpen] = useState(true);

    const companyChanges = getCompanyChanges(currentInfo, proposedInfo);
    const contactChanges = getContactChanges(currentInfo, proposedInfo);
    const affiliationChanges = getAffiliationChanges(currentAffiliations, proposedAffiliations);

    const affiliationChangeCount = affiliationChanges.reduce(
        (total, item) => total + item.changes.length,
        0,
    );

    const totalChanges = companyChanges.length + contactChanges.length + affiliationChangeCount;

    if (!isShow) {
        return null;
    }

    if (totalChanges === 0) {
        return null;
    }

    return (
        <div className="grid grid-cols-1 gap-3.5">
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                {/* MAIN HEADER */}
                <button
                    type="button"
                    onClick={() => setShowContent((previous) => !previous)}
                    className="flex w-full items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2.5 text-left transition-colors hover:bg-slate-50"
                >
                    <div className="flex min-w-0 items-center gap-2">
                        <svg viewBox="0 0 24 24" fill="none" className={`h-3.5 w-3.5 shrink-0 text-[#186cb2] transition-transform ${showContent ? 'rotate-0' : '-rotate-90'}`} aria-hidden="true" >
                            <path d="m7 10 5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                            Basic Information
                        </h2>
                        <span className="rounded-full bg-[#e3f1fa] px-1.5 py-0.5 text-[8px] font-bold text-[#186cb2] ring-1 ring-[#c7e0f3]">
                            {totalChanges}
                        </span>
                    </div>
                    <span className="shrink-0 text-[9px] font-medium text-slate-400">
                        {showContent ? 'Hide' : 'Show'}
                    </span>
                </button>

                {showContent && (
                    <>
                        <div className="space-y-3 p-3 sm:p-4">
                            {/* COMPANY - only render when there are changes */}
                            {companyChanges.length > 0 && (
                                <ComparisonSection
                                    title="Company Information"
                                    changes={companyChanges}
                                    showContent={companyOpen}
                                    onToggle={() => setCompanyOpen((previous) => !previous)}
                                />
                            )}
                            {/* CONTACT - only render when there are changes */}
                            {contactChanges.length > 0 && (
                                <ComparisonSection
                                    title="Contact Person"
                                    changes={contactChanges}
                                    showContent={contactOpen}
                                    onToggle={() => setContactOpen((previous) => !previous)}
                                />
                            )}
                            {/* AFFILIATIONS - only render when there are changes */}
                            {affiliationChanges.length > 0 && (
                                <AffiliationComparisonSection
                                    changes={affiliationChanges}
                                    showContent={affiliationsOpen}
                                    onToggle={() => setAffiliationsOpen((previous) => !previous)}
                                />
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}